/**
 * Social Share Component (Inline)
 * --------------------------------
 * Simple inline social sharing with copy-to-clipboard functionality.
 */

if (!customElements.get('social-share')) {
  customElements.define('social-share', class SocialShare extends HTMLElement {
    constructor() {
      super();

      this.shareUrl = this.dataset.shareUrl || window.location.href;
      this.shareTitle = this.dataset.shareTitle || document.title;

      this.elements = {
        copyButton: this.querySelector('[data-copy-url]'),
        nativeShareButton: this.querySelector('[data-native-share]'),
        statusRegion: this.querySelector('[role="status"]'),
        shareLinks: this.querySelectorAll('a[data-share-platform]')
      };

      this.init();
    }

    init() {
      // Set up copy button
      if (this.elements.copyButton) {
        this.elements.copyButton.addEventListener('click', this.handleCopy.bind(this));
      }

      // Set up native share button (Instagram/mobile share)
      if (this.elements.nativeShareButton) {
        // Hide button if Web Share API not supported
        if (!navigator.share) {
          this.elements.nativeShareButton.classList.add('hidden');
        } else {
          this.elements.nativeShareButton.addEventListener('click', this.handleNativeShare.bind(this));
        }
      }

      // Track share clicks for analytics
      this.elements.shareLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          this.handleShareClick(e, link.dataset.sharePlatform);
        });
      });
    }

    async handleNativeShare() {
      const shareData = {
        title: this.shareTitle,
        text: this.shareTitle,
        url: this.shareUrl
      };

      try {
        await navigator.share(shareData);
        // Track the share for analytics
        this.dispatchEvent(new CustomEvent('share', {
          bubbles: true,
          detail: {
            platform: 'native',
            url: this.shareUrl,
            title: this.shareTitle
          }
        }));
      } catch (err) {
        // User cancelled or share failed - this is normal, don't log as error
        if (err.name !== 'AbortError') {
          console.error('Native share failed:', err);
        }
      }
    }

    async handleCopy() {
      const copyButton = this.elements.copyButton;
      const copyIcon = copyButton.querySelector('.social-share__icon-copy');
      const copiedIcon = copyButton.querySelector('.social-share__icon-copied');
      const copyText = copyButton.querySelector('.social-share__copy-text');
      const copiedText = copyButton.querySelector('.social-share__copied-text');

      try {
        await navigator.clipboard.writeText(this.shareUrl);

        // Visual feedback
        if (copyIcon) copyIcon.classList.add('hidden');
        if (copiedIcon) copiedIcon.classList.remove('hidden');
        if (copyText) copyText.classList.add('hidden');
        if (copiedText) copiedText.classList.remove('hidden');
        copyButton.classList.add('is-copied');

        // Screen reader announcement
        this.announceToScreenReader(window.accessibilityStrings?.shareSuccess || 'Link copied to clipboard');

        // Reset after delay
        setTimeout(() => {
          if (copyIcon) copyIcon.classList.remove('hidden');
          if (copiedIcon) copiedIcon.classList.add('hidden');
          if (copyText) copyText.classList.remove('hidden');
          if (copiedText) copiedText.classList.add('hidden');
          copyButton.classList.remove('is-copied');
        }, 2000);

      } catch (err) {
        console.error('Copy failed:', err);
        // Fallback for older browsers
        this.fallbackCopy(this.shareUrl);
      }
    }

    fallbackCopy(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        this.elements.copyButton.classList.add('is-copied');
        this.announceToScreenReader('Link copied to clipboard');

        setTimeout(() => {
          this.elements.copyButton.classList.remove('is-copied');
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }

      document.body.removeChild(textArea);
    }

    handleShareClick(event, platform) {
      // Dispatch custom event for analytics tracking
      this.dispatchEvent(new CustomEvent('share', {
        bubbles: true,
        detail: {
          platform: platform,
          url: this.shareUrl,
          title: this.shareTitle
        }
      }));
    }

    announceToScreenReader(message) {
      if (this.elements.statusRegion) {
        this.elements.statusRegion.textContent = message;
        // Clear after announcement
        setTimeout(() => {
          this.elements.statusRegion.textContent = '';
        }, 1000);
      }
    }
  });
}
